import { type Cell, type Sheet, parseShownNumber } from './sheet';

/**
 * The tables of the page as it is shown - "Excel from every page" without
 * each page having to describe its data twice. A cell's text becomes a number
 * where it reads as one; a selection field gives its chosen value. Columns
 * that only hold controls (✕, ⌕) are left out.
 */
const CONTROL = /^[✕⌕Σ≡·↶]*$/;

function cellValue(td: HTMLTableCellElement): Cell {
  const input = td.querySelector('input:not([type=checkbox]):not([type=radio]), textarea') as HTMLInputElement | null;
  const text = (input && input.value) || td.innerText.replace(/\s+/g, ' ').replace(/\s*ƒ\s*$/, '').trim();
  if (text === '' || text === '—') return null;
  const n = parseShownNumber(text);
  return n === null ? text : n;
}

function titleFor(table: HTMLTableElement, index: number): string {
  const section = table.closest('.mantine-Paper-root');
  const heading = section?.querySelector('h5, h4, h3')?.textContent?.trim();
  return heading || `Tabelle ${index + 1}`;
}

export function sheetsFromPage(root: HTMLElement, pageTitle: string): Sheet[] {
  const tables = [...root.querySelectorAll('table')].filter((t) => t.offsetParent !== null && !t.closest('.mantine-Modal-root'));
  return tables.map((table, index) => {
    const head = [...(table.tHead?.rows[0]?.cells ?? [])].map((c) => c.innerText.replace(/\s+/g, ' ').trim());
    const body = [...table.tBodies].flatMap((tb) => [...tb.rows]).filter((r) => r.offsetParent !== null);
    const rows = body.map((tr) => {
      const out: Cell[] = [];
      for (const td of [...tr.cells]) {
        out.push(cellValue(td));
        for (let k = 1; k < td.colSpan; k++) out.push(null);
      }
      return out;
    });
    const width = Math.max(head.length, ...rows.map((r) => r.length));
    // Drop columns without a heading that hold nothing but controls.
    const keep = [...Array(width).keys()].filter((i) => (head[i] ?? '') !== '' || rows.some((r) => r[i] !== null && !CONTROL.test(String(r[i]))));
    // Group and total lines: drawn bold or with a rule above.
    const bold = body
      .map((tr, i) => {
        const strong = tr.querySelector('p, span, div');
        const weight = Number(getComputedStyle(strong ?? tr).fontWeight);
        return weight >= 600 || tr.style.borderTop !== '' ? i : -1;
      })
      .filter((i) => i >= 0);
    const title = titleFor(table, index);
    return {
      name: title,
      title: `${pageTitle} – ${title}`,
      columns: keep.map((i) => ({
        label: head[i] ?? '',
        format: rows.some((r) => typeof r[i] === 'number' && !Number.isInteger(r[i])) ? 'amount' : rows.some((r) => typeof r[i] === 'number') ? 'quantity' : undefined,
      })),
      rows: rows.map((r) => keep.map((i) => r[i] ?? null)),
      boldRows: bold,
    } satisfies Sheet;
  }).filter((s) => s.rows.length > 0);
}
